import { signInWithPopup, getAuth, AuthProvider } from 'firebase/auth';
import { googleProvider, githubProvider } from '../firebase/firebase';

/**
 * Authentication providers configuration
 */
export type AuthProviderType = 'google' | 'github' | 'linkedin';

/**
 * Get the appropriate Firebase auth provider based on provider type
 */
export const getProvider = (providerType: AuthProviderType): AuthProvider => {
  switch (providerType) {
    case 'google':
      return googleProvider;
    case 'github':
      return githubProvider;
    default:
      throw new Error(`Provider ${providerType} not supported`);
  }
};

/**
 * Handle authentication with a third-party provider
 * @param providerType The authentication provider to use
 * @returns User information from the provider
 */
export const handleProviderAuth = async (providerType: AuthProviderType) => {
  const auth = getAuth();
  const provider = getProvider(providerType);
  const result = await signInWithPopup(auth, provider);

  // Extract user info from the result
  const { user } = result;
  const { uid, email, displayName } = user;

  // Different providers have different username formats
  let username = '';

  if (providerType === 'google') {
    // For Google, use the part before @ in email
    username = email?.split('@')[0] || '';
  } else if (providerType === 'github') {
    // For GitHub, use the display name or the uid as fallback
    username = displayName?.replace(/\s+/g, '') || `github_${uid.substring(0, 8)}`;
  } else if (providerType === 'linkedin') {
    // For LinkedIn, use 'li_' prefix with displayName or uid
    username = displayName
      ? `linkedin_${displayName.toLowerCase().replace(/\s+/g, '')}`
      : `linkedin_${uid.substring(0, 8)}`;
  }

  // Use uid as password for our system
  const password = uid;

  return {
    username,
    password,
    email: email || '',
    isProviderAuth: true,
    provider: providerType,
  };
};
